import { Box, Paper, Typography } from '@mui/material';
import type {
  CellStyle,
  ColDef,
  GetDataPath,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowClickedEvent
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue } from 'jotai';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import { useFetchDevicesByOrg } from '@/features/DeviceCreate/DeviceCreate.hooks';
import useGridTheme from '@/hooks/useGridTheme';
import {
  CustomEventTypes,
  customEventAtom,
  devicesAtom,
  groupedDevicesAtom,
  updateDeviceDTO
} from '@/pages/Administration/Devices';
import useDeviceQueryParam from '@/pages/Administration/Devices/Devices.hooks';
import type {
  DeviceListingResponseDTO,
  DeviceSyncDataDTO
} from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { createAdminDeviceColumnDefs } from './AdminDevicesTable.constant';
import useStyles from './DevicesTable.styles';
import type {
  DeviceDTO,
  DeviceModel,
  DevicesTableProps
} from './DevicesTable.types';
import {
  type AdminTreeDataRow,
  convertToAdminTreeData,
  groupDevicesByType
} from './utils/adminTreeData.utils';

/**
 * Get data path for tree data structure
 * @returns Function that extracts path array from data row
 */
const getDataPath = (): GetDataPath => (data: AdminTreeDataRow) => data.path;

/**
 * Auto group column definition for device type groups
 * @param renderGroupHeader - Function to render group header
 * @returns Column definition for auto-group column
 */
const getAdminDevicesAutoGroupColumnDef = (
  renderGroupHeader: (params: ICellRendererParams) => React.ReactNode
): ColDef => ({
  minWidth: 0,
  width: 0,
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: true,
    suppressEnterExpand: true,
    suppressExpandCollapse: true,
    innerRenderer: renderGroupHeader
  },
  cellStyle: {
    paddingLeft: '10px'
  }
});

/**
 * Default column definition for admin devices grid
 * @returns Default column settings
 */
const getAdminDevicesDefaultColDef = (): ColDef => ({
  resizable: true,
  sortable: true,
  filter: false,
  floatingFilter: false,
  suppressHeaderMenuButton: true,
  cellStyle: (params): CellStyle => {
    if (!params.colDef.field || params.node.group) {
      return {};
    }
    return { cursor: 'pointer' };
  }
});

/**
 * Grid options for admin devices grid
 * @param autoGroupColumnDef - Auto group column configuration
 * @param getDataPathMemo - Memoized data path getter
 * @returns Grid options object
 */
const getAdminDevicesGridOptions = (
  autoGroupColumnDef: ColDef,
  getDataPathMemo: GetDataPath
): GridOptions => ({
  treeData: true,
  animateRows: true,
  groupDefaultExpanded: -1,
  getDataPath: getDataPathMemo,
  autoGroupColumnDef,
  suppressRowClickSelection: true,
  rowSelection: 'single',
  suppressCellFocus: true,
  enableCellTextSelection: true,
  ensureDomOrder: true,
  suppressContextMenu: true,
  suppressRowTransform: false
});

/**
 * Get row class for styling selected rows
 * @param params - Row class params from AG Grid
 * @returns Array of CSS class names
 */
const getAdminDevicesRowClass = (params: {
  node: { isSelected: () => boolean | undefined };
}): string[] => {
  const classes: string[] = [];

  if (params.node.isSelected()) {
    classes.push('agGridDeviceTableRowSelected');
  }

  return classes;
};

/**
 * AdminDevicesTable Component
 *
 * Displays devices grouped by device type in a tree structure.
 * Used in the administration panel for device management.
 *
 * Features:
 * - Tree structure grouped by device type
 * - Row selection and navigation
 * - Sorting by columns
 * - Auto-refresh every 30 seconds
 * - Pagination
 * - Custom events for refresh triggers
 *
 * @param props - Component props
 * @returns JSX element with AG Grid table
 */
const AdminDevicesTable = ({
  closeDeviceDetail,
  isMainDevicePage
}: DevicesTableProps) => {
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const { classes } = useStyles();
  const gridTheme = useGridTheme();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [, setDevices] = useAtom(devicesAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const customEventValue = useAtomValue(customEventAtom);
  const [groupedDevices, setGroupedDevices] = useAtom(groupedDevicesAtom);
  const gridRef = useRef<AgGridReact>(null);

  const { addDeviceFilter, deviceUid } = useDeviceQueryParam();

  const {
    isError,
    isFetching,
    isFetched,
    isSuccess,
    isRefetching,
    isLoading,
    data,
    refetch
  } = useFetchDevicesByOrg(selectedOrgData);

  // Convert grouped devices to tree data format
  const treeData = useMemo<AdminTreeDataRow[]>(() => {
    return convertToAdminTreeData(groupedDevices);
  }, [groupedDevices]);

  const filteredTreeRowIds = useMemo(() => {
    return new Set(
      treeData.map((row) =>
        row.isGroupRow
          ? `group:${row.path?.[0] || ''}`
          : `device:${row.device_uid || row.path?.join('/') || ''}`
      )
    );
  }, [treeData]);

  const reconcileSelectionFromUrl = useCallback(
    (api: GridApi<AdminTreeDataRow>) => {
      if (!deviceUid) {
        api.deselectAll();
        api.redrawRows();
        return;
      }

      const selectedRowId = `device:${deviceUid}`;
      const node = api.getRowNode(selectedRowId);
      if (node) {
        node.setSelected(true, true);
        api.redrawRows();
        return;
      }

      if (isLoading || isFetching || isRefetching || !isFetched) {
        api.redrawRows();
        return;
      }

      if (!filteredTreeRowIds.has(selectedRowId)) {
        api.deselectAll();
        api.redrawRows();
        closeDeviceDetail();
      }
    },
    [
      closeDeviceDetail,
      deviceUid,
      filteredTreeRowIds,
      isFetched,
      isFetching,
      isLoading,
      isRefetching
    ]
  );

  // Column definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    return createAdminDeviceColumnDefs(
      isMainDevicePage ?? false,
      selectedOrgData.id
    );
  }, [isMainDevicePage, selectedOrgData.id]);

  // Get data path for tree data
  const getDataPathMemo = useMemo(() => getDataPath(), []);

  // Auto group column definition
  const autoGroupColumnDef = useMemo(
    () =>
      getAdminDevicesAutoGroupColumnDef((params) => {
        if (params.node.group && params.node.level === 0) {
          return (
            <Typography variant="h5" sx={{ padding: '.75rem 0' }}>
              {params.value}
            </Typography>
          );
        }
        return null;
      }),
    []
  );

  // Default column definition
  const defaultColDef = useMemo(() => getAdminDevicesDefaultColDef(), []);

  // Grid options
  const gridOptions = useMemo(
    () => getAdminDevicesGridOptions(autoGroupColumnDef, getDataPathMemo),
    [autoGroupColumnDef, getDataPathMemo]
  );

  /**
   * Update device state from API response
   * @param deviceData - Array of device models
   */
  const updateDeviceState = useCallback(
    (deviceData: DeviceModel[]) => {
      setDevices(deviceData);
      const grouped = groupDevicesByType(deviceData);
      setGroupedDevices(grouped);
    },
    [setDevices, setGroupedDevices]
  );

  /**
   * Handle row click - navigate to device detail
   */
  const onRowClicked = useCallback(
    (event: RowClickedEvent) => {
      const { data: rowData, node } = event;

      if (rowData && !rowData.isGroupRow && rowData.device_uid) {
        node.setSelected(true);
        addDeviceFilter(rowData.device_uid);
      }
    },
    [addDeviceFilter]
  );

  /**
   * Handle grid ready event - apply initial sort
   */
  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.applyColumnState({
      state: [
        {
          colId: 'last_seen_display',
          sort: 'desc'
        }
      ]
    });
    reconcileSelectionFromUrl(params.api as GridApi<AdminTreeDataRow>);
  }, []);

  useEffect(() => {
    const api = gridRef.current?.api as GridApi<AdminTreeDataRow> | undefined;
    if (!api) {
      return;
    }

    reconcileSelectionFromUrl(api);
  }, [deviceUid, treeData, reconcileSelectionFromUrl]);

  // Process API response and update device state
  useEffect(() => {
    const devicesResponse = data as unknown as DeviceListingResponseDTO;

    if (isSuccess && data && devicesResponse?.other_devices) {
      const devicesDeployedData =
        (devicesResponse?.other_devices as DeviceSyncDataDTO[]) || [];
      const devicesData: DeviceDTO[] = [
        ...devicesDeployedData
          .filter((deviceRow) => deviceRow.devices !== null)
          .map((deviceRow) => deviceRow.devices as DeviceDTO)
      ];
      let deviceModelData = updateDeviceDTO(devicesData as DeviceModel[]);
      deviceModelData = orderBy(deviceModelData, 'device_type_id', 'asc');
      updateDeviceState(deviceModelData);
    } else {
      setDevices([]);
      setGroupedDevices([]);
    }
  }, [
    isFetching,
    isFetched,
    isSuccess,
    isRefetching,
    isError,
    updateDeviceState,
    setDevices,
    setGroupedDevices
  ]);

  // Auto-refresh timer and organization change handling
  useEffect(() => {
    closeDeviceDetail();
    if (selectedOrgData.id >= 0 && !isFetching) {
      refetch();
    }

    // Auto Refresh Timer to update Device Status every 30 seconds
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      if (selectedOrgData.id >= 0) {
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_DETAIL);
        refetch();
      }
    }, 30000); // 30 seconds

    // Clear Auto Refresh Timer on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [selectedOrgData.id]);

  // Handle custom events for refresh
  useEffect(() => {
    if (customEventValue === CustomEventTypes.REFRESH_DEVICE_LISTING) {
      setCustomEvent(CustomEventTypes.NONE);
      if (!isFetching) {
        refetch();
      }
    } else if (customEventValue === CustomEventTypes.CLOSE_DEVICE_DETAIL) {
      setCustomEvent(CustomEventTypes.NONE);
      closeDeviceDetail();
    }
  }, [
    customEventValue,
    isFetching,
    refetch,
    closeDeviceDetail,
    setCustomEvent
  ]);

  return (
    <Paper className={classes.deviceTableContainer}>
      <LoadingIndicator isLoading={isLoading} />
      <Box sx={{ height: '100%', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          rowData={treeData}
          getRowId={(params) =>
            params.data?.isGroupRow
              ? `group:${params.data.path?.[0] || ''}`
              : `device:${params.data?.device_uid || params.data?.path?.join('/') || ''}`
          }
          rowHeight={66}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onRowClicked={onRowClicked}
          onGridReady={onGridReady}
          overlayNoRowsTemplate='<span style="padding: 1rem;">No matches found</span>'
          getRowClass={getAdminDevicesRowClass}
        />
      </Box>
    </Paper>
  );
};

export default AdminDevicesTable;
